import React from 'react';
import { range } from 'lodash-es';

import { Box, Divider, FormField, Input, Label, Paragraph, TextButton } from '@zillow/constellation-10';
import { IconPlusCircleFilled } from '@zillow/constellation-icons';

import type { Section } from '../../../../models';
import { Field, SmokingPolicySection } from '../../../../models';
import { SMOKING_POLICY } from '../../../../constants';
import { useIndexedSection } from '../../../../hooks';

const MAXIMUM_SMOKING_AREAS = 5;

type Props = {
  section: Section;
  setSection: (section: SmokingPolicySection) => void;
  setSectionValues: (keyValuePairs: object) => void;
};

export default function SmokingAdditionalAreas({ section, setSection, setSectionValues }: Props) {
  const { onAddOne, onDeleteOne } = useIndexedSection({
    section,
    setSection,
    SectionClass: SmokingPolicySection,
  });

  function setCustomDescription(e: React.ChangeEvent<HTMLInputElement>): void {
    const { index } = e.currentTarget.dataset;
    const fieldName = Field.getIndexedFieldName(SMOKING_POLICY.FIELD_NAME.CUSTOM_AREA_DESCRIPTION, Number(index));

    setSectionValues({ [fieldName]: e.currentTarget.value });
  }

  function renderArea(index: number): React.ReactNode {
    const descriptionFieldName = Field.getIndexedFieldName(SMOKING_POLICY.FIELD_NAME.CUSTOM_AREA_DESCRIPTION, index);

    const descriptionValue = section.getFieldValue(descriptionFieldName);

    return (
      <React.Fragment key={index}>
        {index !== MAXIMUM_SMOKING_AREAS && (
          <Divider css={{ marginTop: 'layout.tighter', marginBottom: 'layout.tighter' }} />
        )}
        <FormField
          required
          label={
            <Box css={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'layout.tighter' }}>
              <Label>Area #{index + 1}</Label>
              <TextButton
                aria-label={`Delete area #${index + 1}`}
                data-test="delete-link"
                data-index={index}
                onClick={onDeleteOne}
                textStyle="body-sm-bold"
              >
                Delete
              </TextButton>
            </Box>
          }
          control={
            <Input
              aria-label={`Area #${index + 1}`}
              data-index={index}
              data-test="input-description"
              value={descriptionValue}
              onChange={setCustomDescription}
            />
          }
        />
      </React.Fragment>
    );
  }

  const totalAreas = section.getTotalIndexes();

  return (
    <>
      {range(totalAreas).map((index: number) => (
        <React.Fragment key={index}>{renderArea(index)}</React.Fragment>
      ))}
      {totalAreas < MAXIMUM_SMOKING_AREAS ? (
        totalAreas >= 0 && (
          <TextButton
            onClick={onAddOne}
            icon={<IconPlusCircleFilled />}
            css={{ marginTop: 'layout.tight' }}
            data-test="add-another"
          >
            Add another area
          </TextButton>
        )
      ) : (
        <Paragraph css={{ marginTop: 'layout.tight' }}>
          You’ve added {MAXIMUM_SMOKING_AREAS} additional areas, which is the maximum allowed.
        </Paragraph>
      )}
    </>
  );
}
